import { useState } from 'react';
import { KeyRound, Loader2, Eye, EyeOff } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';
import { toast } from 'sonner';

const MIN_LEN = 12;

function strengthScore(pw) {
  let s = 0;
  if (pw.length >= MIN_LEN) s += 1;
  if (pw.length >= 16) s += 1;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) s += 1;
  if (/\d/.test(pw)) s += 1;
  if (/[^A-Za-z0-9]/.test(pw)) s += 1;
  return s; // 0..5
}

const STRENGTH_LABELS = ['Very weak', 'Weak', 'Fair', 'Good', 'Strong', 'Excellent'];

export default function ChangePasswordDialog({ open, onOpenChange, mustChange = false }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const score = strengthScore(next);

  const reset = () => {
    setCurrent('');
    setNext('');
    setConfirm('');
    setShow(false);
    setError('');
  };

  const onSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    if (next.length < MIN_LEN) {
      setError(`New password must be at least ${MIN_LEN} characters.`);
      return;
    }
    if (next !== confirm) {
      setError('New password and confirmation do not match.');
      return;
    }
    if (next === current) {
      setError('New password must differ from current password.');
      return;
    }
    setLoading(true);
    try {
      await api.post(
        '/admin/change-password',
        { current_password: current, new_password: next },
      );
      try {
        sessionStorage.removeItem('klymart_must_change_password');
      } catch (err) {
        // sessionStorage may be unavailable (private mode); the flag will be cleared
        // on the next reload anyway since the DB hash now exists.
        if (process.env.NODE_ENV === 'development') {
          console.debug('[admin] sessionStorage unavailable, could not clear must-change flag:', err?.message);
        }
      }
      toast.success('Password updated.');
      reset();
      onOpenChange(false);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not update password.';
      setError(typeof msg === 'string' ? msg : 'Could not update password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        // If first-login forced change, don't allow closing without success
        if (!o && mustChange) return;
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent
        data-testid="admin-change-password-dialog"
        className={
          'bg-card border border-border max-w-md ' +
          (mustChange ? '[&>button.absolute]:hidden' : '')
        }
      >
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg border border-border bg-secondary flex items-center justify-center text-primary">
              <KeyRound className="h-4 w-4" />
            </div>
            <div>
              <DialogTitle className="font-display text-xl">Change password</DialogTitle>
              <DialogDescription className="text-xs">
                {mustChange
                  ? 'You\u2019re still on the bootstrap password. Please set your own.'
                  : 'Update your admin password. You\u2019ll stay signed in.'}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={onSubmit} noValidate className="space-y-3 mt-2">
          <div>
            <Label htmlFor="cp-current" className="text-xs uppercase tracking-wider text-muted-foreground">
              Current password
            </Label>
            <Input
              id="cp-current"
              data-testid="admin-cp-current"
              type={show ? 'text' : 'password'}
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className="mt-1.5 h-10 bg-secondary/50 border-border focus-visible:ring-ring"
              autoFocus
            />
          </div>

          <div>
            <Label htmlFor="cp-next" className="text-xs uppercase tracking-wider text-muted-foreground">
              New password
            </Label>
            <div className="relative mt-1.5">
              <Input
                id="cp-next"
                data-testid="admin-cp-next"
                type={show ? 'text' : 'password'}
                value={next}
                onChange={(e) => setNext(e.target.value)}
                className="h-10 pr-10 bg-secondary/50 border-border focus-visible:ring-ring"
              />
              <button
                type="button"
                onClick={() => setShow((v) => !v)}
                aria-label={show ? 'Hide passwords' : 'Show passwords'}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1"
              >
                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {/* Strength meter */}
            <div className="mt-2 flex items-center gap-1.5">
              {[0, 1, 2, 3, 4].map((i) => (
                <span
                  key={i}
                  className={`h-1 flex-1 rounded-full transition-colors ${
                    score > i ? (score >= 4 ? 'bg-accent' : 'bg-primary') : 'bg-border'
                  }`}
                />
              ))}
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground ml-1">
                {STRENGTH_LABELS[score] || 'Very weak'}
              </span>
            </div>
            <p className="mt-1 text-[10px] text-muted-foreground">
              Minimum {MIN_LEN} characters · mix of letters, digits and symbols recommended.
            </p>
          </div>

          <div>
            <Label htmlFor="cp-confirm" className="text-xs uppercase tracking-wider text-muted-foreground">
              Confirm new password
            </Label>
            <Input
              id="cp-confirm"
              data-testid="admin-cp-confirm"
              type={show ? 'text' : 'password'}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="mt-1.5 h-10 bg-secondary/50 border-border focus-visible:ring-ring"
            />
          </div>

          {error && (
            <div data-testid="admin-cp-error" className="text-sm text-destructive">
              {error}
            </div>
          )}

          <DialogFooter className="!mt-5">
            {!mustChange && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={loading}
                className="h-10 rounded-lg"
              >
                Cancel
              </Button>
            )}
            <Button
              type="submit"
              data-testid="admin-cp-submit"
              disabled={loading || !current || !next || !confirm}
              className="h-10 px-5 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                  Updating…
                </>
              ) : (
                <>
                  <KeyRound className="h-4 w-4 mr-1.5" />
                  Update password
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
